import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { cloneDeep, isEmpty, xor } from 'lodash';
import Styles from '../AuthorGroups.css';
import Select from 'zignal-landscape/lib/Select';
import ToolTip from 'zignal-landscape/lib/ToolTip';
import DuplicateIcon from 'zignal-landscape/lib/Icon/DuplicateIcon';
import TrashIcon from 'zignal-landscape/lib/Icon/TrashIcon';
import Add3Icon from 'zignal-landscape/lib/Icon/Add3Icon';
import { ALL_AUTHOR_GROUP_TARGETS } from '../AuthorGroupsUtils';
import { AuthorGroupsContext } from '../AuthorGroups';

const DELIMITER = ';';

const DEFAULT_MEMBER = {};

const AuthorGroupMember = ({
  isReadOnly,
  memberKey,
  member = DEFAULT_MEMBER
}) => {
  const { state, dispatch } = useContext(AuthorGroupsContext);
  const [ authors, setAuthors ] = useState(member.authors ? [...member.authors] : []); // authors or domains or media-outlets
  const [ isAuthorsElementDisabled, setIsAuthorsElementDisabled ] = useState(isEmpty(member) || isEmpty(member.value));

  useEffect(() => {
    setAuthors(member.authors ? [...member.authors] : []);
    setIsAuthorsElementDisabled(isEmpty(member.value));

    return () => setAuthors();
  }, [ member ]);

  const isNew = isEmpty(member);

  const canAddMember = () => {
    if (isReadOnly) {
      return false;
    }
    if (isEmpty(state.selectedAuthorGroup.members)) {
      return true;
    }
    const keys = state.memberKeyList;
    const lastMember = memberKey === keys[keys.length - 1];
    const moreOptionsRemaining = keys.length < ALL_AUTHOR_GROUP_TARGETS.length;
    return lastMember && moreOptionsRemaining;
  };

  const handleAuthorGroupSelection = (selection) => {
    setAuthors([]);
    setIsAuthorsElementDisabled(false);
    const sag = cloneDeep(state.selectedAuthorGroup);
    sag.members[memberKey] = { ...selection, authors: []};
    dispatch({type: 'set-selected-author-group', authorGroup: sag}); //updates the counts and other meta data
  };

  const handleAuthorsChange = (updatedAuthorsMultiSelect) => {
    const updatedAuthors = updatedAuthorsMultiSelect
      .map(selection => {
        const value = member.type === 'media_outlet' ? selection.value : selection.value.replaceAll(',', DELIMITER);
        return value
          .split(DELIMITER)
          .map(author => author.trim());
      })
      .flat()
      .filter(author => author !== '');

    // ensure there are no duplicates
    const uniqueValues = Array.from(new Set(updatedAuthors));
    if (xor(uniqueValues, authors).length === 0) {
      // uniqueValues and authors have the same unique values
      return authors;
    }
    setAuthors([...uniqueValues]);
    const sag = cloneDeep(state.selectedAuthorGroup);
    sag.members[memberKey] = { ...member, authors: uniqueValues};
    dispatch({type: 'set-selected-author-group', authorGroup: sag}); //updates the counts and other meta data
  };

  const handleDeleteMember = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const sag = cloneDeep(state.selectedAuthorGroup);
    delete sag.members[memberKey];
    if (isEmpty(sag.members)) {
      sag.members['0'] = {}; // add a blank member
    }
    dispatch({type: 'set-selected-author-group', authorGroup: sag}); //updates the counts and other meta data
  };

  const handleAddMember = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authors.length) {
      /*
       * Disallow addition of new members unless author group dropdown
       * is selected and at least one author is added to avoid the
       * possibility of members with the same author group targets.
       */
      return;
    }
    dispatch({type: 'add-blank-author-group-member'});
  };

  const getAuthorsMultiSelectValue = () => {
    if (isNew || isEmpty(authors)) {
      return null;
    }
    return authors.map(a => ({value: a, label: a}));
  };

  const handleCopyMember = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const values = getAuthorsMultiSelectValue().map(elem => elem.value);
    navigator.clipboard.writeText(`${values.join(';')};`);
    Account.toastrNotifications._successMessage({
      success_title: '',
      success_message: 'The selected authors have been copied to your clipboard.'
    });
  };

  // delimiter parsing on input
  const handleAuthorsInputChange = (inputValue, { action }) => {
    if (action === 'input-change' && inputValue) {
      const normalizedInputValue = member.type !== 'media_outlet'
        ? inputValue.replaceAll(',', DELIMITER)
        : inputValue;
      if (normalizedInputValue.includes(DELIMITER)) {
        const parts = normalizedInputValue.split(DELIMITER);
        const lastPart = parts.pop() || '';
        const newParts = parts.map(p => p.trim()).filter(p => p !== '');
        if (newParts.length > 0) {
          const newOptions = newParts.map(val => ({ value: val, label: val }));
          const currentOptions = getAuthorsMultiSelectValue() || [];
          handleAuthorsChange([ ...currentOptions, ...newOptions ]);
        }
        return lastPart.trim();
      }
    }
    return inputValue;
  };

  return (
    <div className={Styles.authorGroupMember} data-cy="author-group-member">
      <Select
        className={Styles.authorGroupMemberDropDown}
        value={isNew ? null : member}
        placeholder={'Select Target ...'}
        options={state.unusedAuthorGroupTargets}
        searchable
        isDisabled={isReadOnly}
        onChange={handleAuthorGroupSelection}
        selectStyles={{
          control: base => ({
            ...base,
            minHeight: '2.125rem',
            maxHeight: '2.125rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            backgroundColor: '#EFF1F3'
          }),
          valueContainer: base => ({
            ...base,
            minHeight: '2rem',
            maxHeight: '2rem'
          }),
          singleValue: base => ({
            ...base,
            padding: 0,
            lineHeight: '2rem',
            display: 'flex',
            alignItems: 'center'
          })
        }}
      />
      <Select
        className={Styles.authors}
        placeholder={''}
        options={[]}
        size={'normal'}
        value={getAuthorsMultiSelectValue()}
        allowCreate
        isMulti
        showArrow={false}
        blurInputOnSelect={false}
        isDisabled={isReadOnly || isAuthorsElementDisabled}
        onChange={handleAuthorsChange}
        onInputChange={handleAuthorsInputChange}
      />
      <div className={Styles.authorGroupMemberRightmost}>
        <span className={Styles.authorsCount}>{authors.length}</span>
        <ToolTip tooltipText="Copy Authors">
          <DuplicateIcon
            className={Styles.removeAuthorGroupMember}
            onClick={handleCopyMember}
            visibility={isReadOnly ? 'hidden' : 'visible'}
          />
        </ToolTip>
        <ToolTip tooltipText="Remove Row">
          <TrashIcon
            className={Styles.removeAuthorGroupMember}
            onClick={handleDeleteMember}
            visibility={isReadOnly ? 'hidden' : 'visible'}
          />
        </ToolTip>
        <ToolTip tooltipText="Add Row">
          <Add3Icon
            className={Styles.addAuthorGroupMember}
            visibility={canAddMember() ? 'visible' : 'hidden'}
            onClick={handleAddMember}
          />
        </ToolTip>
      </div>
    </div>
  );
};

AuthorGroupMember.propTypes = {
  isReadOnly: PropTypes.bool,
  memberKey: PropTypes.string.isRequired,
  member: PropTypes.object
};

export default AuthorGroupMember;
